import { Button } from "design-system/atoms/Button"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import config from "lib/config"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useEffect, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CampaignDetailResponseDataSchema,
  ConversationFlowEnum,
  PublicBulkProcessTriggerSchema,
  PublicProcessBulkTriggerJsonSchemaSchema,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"

const outboundPayloadExample = {
  triggers: [
    {
      customer: {
        name: "John Doe",
        phone: "+34612345678",
        country: "ES",
        locale: "es-ES",
        metadata: {
          age: 30,
          location: "USA",
          orderId: "1234567890",
        },
      },
      closeOngoingConversations: true,
      delayInHours: 0,
      event: {
        name: "order_created",
        order_id: "1234567890",
        order_name: "Order 1234567890",
        products: [
          {
            sku: "1234567890",
            name: "Product 1234567890",
            price: 100,
            quantity: 1,
          },
        ],
        date: "2025-01-01T00:00:00Z",
      },
      actions: [
        {
          type: "trustpilot" as const,
          link: "https://www.trustpilot.com/review/example.com",
        },
      ],
    },
  ],
} satisfies z.input<typeof PublicBulkProcessTriggerSchema>

const inboundPayloadExample = {
  triggers: [
    {
      referenceId: "ref-1234567890",
      message: "Hi, I need help with order #1234567890.",
      agent: "support@ecommerce.com",
      orderId: "#1234567890",
      event: {},
      customer: {
        name: "John Doe",
        phone: "+34612345678",
        country: "ES",
        locale: "es-ES",
        metadata: {
          age: 30,
          location: "USA",
          orderId: "1234567890",
        },
        client: {
          externalId: "client-ext-1234",
          name: "Ecommerce SL",
        },
      },
    },
  ],
} satisfies z.input<typeof PublicBulkProcessTriggerSchema>

type Props = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

export function WebhookMetadataInfo({ campaign }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()

  const conversationFlow = form.watch("flow") ?? campaign.flow ?? ConversationFlowEnum.Outbound

  useEffect(() => {
    if (!form.getValues("flow")) {
      form.setValue("flow", campaign.flow ?? ConversationFlowEnum.Outbound)
      form.trigger()
    }
  }, [form, campaign.flow])

  const examplePayload = useMemo(
    () =>
      conversationFlow === ConversationFlowEnum.Inbound
        ? inboundPayloadExample
        : outboundPayloadExample,
    [conversationFlow],
  )

  const urlEndpoint = `${config.apiEndpoint}/api/v1/c/${campaign.uuid}`

  const url = `\`\`\`yml
${urlEndpoint}
\`\`\`
`

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {campaign.uuid && (
          <MarkdownRenderer className="text-sm **:break-all **:whitespace-pre-wrap">
            {url}
          </MarkdownRenderer>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm text-muted-foreground">
          {t("campaigns.trigger.metadata.webhook.descriptionExample")}
        </span>
        <MarkdownRenderer className="text-sm">
          {`\`\`\`json
${JSON.stringify(examplePayload, null, 2)}
\`\`\``}
        </MarkdownRenderer>
        <div className="flex items-start">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const jsonSchema = z.toJSONSchema(PublicProcessBulkTriggerJsonSchemaSchema)
              navigator.clipboard.writeText(JSON.stringify(jsonSchema, null, 2))
              toast.success(t("campaigns.trigger.metadata.webhook.copyJsonSchemaSuccess"))
            }}
          >
            {t("campaigns.trigger.metadata.webhook.copyJsonSchema")}
          </Button>
        </div>
      </div>
    </div>
  )
}
